import React, { useState, useEffect, Component } from 'react';
import quizData from './quizData';

// ErrorBoundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  // This lifecycle method is called after an error is thrown by a descendant component.
  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  // This lifecycle method is good for logging errors.
  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-red-100 border-2 border-red-400 rounded-lg shadow-lg text-red-800">
          <h2 className="text-3xl font-bold mb-4">Oops! Something went wrong.</h2>
          <p className="text-lg mb-6">
            We're sorry, but there was an unexpected error. Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-full shadow-lg transition duration-300 ease-in-out hover:scale-105"
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main App component
const App = () => {
  // State to manage which screen is currently displayed
  const [currentScreen, setCurrentScreen] = useState('welcome'); // 'welcome', 'quiz', 'results'
  // State to track current question index
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  // State to track user's score
  const [score, setScore] = useState(0);
  // State to control feedback display after an answer
  const [showFeedback, setShowFeedback] = useState(false);
  // State to indicate if the user's last answer was correct
  const [isCorrect, setIsCorrect] = useState(false);
  // State to hold the current set of quiz questions (randomized subset)
  const [currentQuizSet, setCurrentQuizSet] = useState([]);

  // Function to shuffle an array (Fisher-Yates algorithm)
  const shuffleArray = (array) => {
    let currentIndex = array.length, randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex], array[currentIndex]];
    }
    return array;
  };

  // Function to handle quiz restart
  const startQuiz = () => {
    // Shuffle the full quizData and take the first 10
    const shuffledAndSelected = shuffleArray([...quizData]).slice(0, 5);
    setCurrentQuizSet([...shuffledAndSelected, quizData[0],]);

    setCurrentQuestionIndex(0);
    setScore(0);
    setShowFeedback(false);
    setIsCorrect(false);
    setCurrentScreen('quiz');
  };

  // Function to handle user's answer selection
  const handleAnswerClick = (isAIGeneratedGuess) => {
    // Only allow answering if feedback is not already shown
    if (showFeedback) return;

    const currentMedia = currentQuizSet[currentQuestionIndex];
    const correct = currentMedia.isAIGenerated === isAIGeneratedGuess;
    setIsCorrect(correct);

    if (correct) {
      setScore(prevScore => prevScore + 1);
    }
    setShowFeedback(true);
  };

  // Function to move to the next question or results
  const handleNextClick = () => {
    setShowFeedback(false);
    setIsCorrect(false);

    if (currentQuestionIndex < currentQuizSet.length - 1) { // Use currentQuizSet.length here
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
    } else {
      setCurrentScreen('results'); // End of quiz, show results
    }
  };

  // Render different screens based on currentScreen state
  return (
    <ErrorBoundary> {/* Wrap the entire app content with ErrorBoundary */}
      <div className="min-h-screen bg-gradient-to-br from-blue-100 to-indigo-200 flex items-center justify-center p-4 font-sans antialiased">
        {/* Updated max-width for desktop to utilize more space */}
        <div className="bg-white p-6 md:p-12 rounded-xl shadow-2xl w-full max-w-4xl lg:max-w-7xl text-center flex flex-col items-center">
          {currentScreen === 'welcome' && (
            <WelcomeScreen onStartQuiz={startQuiz} />
          )}

          {currentScreen === 'quiz' && (
            <QuizScreen
              currentMedia={currentQuizSet[currentQuestionIndex]} // Use currentQuizSet here
              currentQuestionIndex={currentQuestionIndex}
              totalQuestions={currentQuizSet.length} // Use currentQuizSet.length here
              score={score}
              onAnswerClick={handleAnswerClick}
              onNextClick={handleNextClick}
              showFeedback={showFeedback}
              isCorrect={isCorrect}
            />
          )}

          {currentScreen === 'results' && (
            <ResultScreen
              score={score}
              totalQuestions={currentQuizSet.length} // Use currentQuizSet.length here
              onRestartQuiz={startQuiz}
            />
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

// Welcome Screen Component
const WelcomeScreen = ({ onStartQuiz }) => {
  return (
    <div className="flex flex-col items-center">
      <h1 className="text-4xl md:text-5xl font-extrabold text-blue-800 mb-6 leading-tight">
        Detecting Fakes Online
      </h1>
      <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-prose">
        Welcome to the AI Fake Detector Quiz! In today's digital world, it's becoming
        harder to tell what's real and what's created by AI. This quiz is designed to
        help you sharpen your skills in spotting AI-generated content, especially
        images and videos.
      </p>
      <p className="text-md md:text-lg text-gray-600 mb-10 max-w-prose">
        Let's learn to be more critical of what we see online and protect ourselves
        from misinformation.
      </p>
      <button
        onClick={onStartQuiz}
        className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-full shadow-lg transform transition duration-300 ease-in-out hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-300"
      >
        Start Quiz
      </button>
    </div>
  );
};

// Quiz Screen Component
const QuizScreen = ({
  currentMedia,
  currentQuestionIndex,
  totalQuestions,
  score,
  onAnswerClick,
  onNextClick,
  showFeedback,
  isCorrect,
}) => {
  const [mediaLoadError, setMediaLoadError] = useState(false);

  // Reset mediaLoadError when currentMedia changes
  useEffect(() => {
    setMediaLoadError(false);
  }, [currentMedia]);

  const tipsContent = (
    <div className="bg-grey-50 p-5 rounded-lg shadow-inner w-full border border-grey-200">
      <h3 className="text-2xl font-bold text-grey-700 mb-3">
        General Tips for Spotting AI Generated Content
      </h3>
      <ul className="list-disc list-inside space-y-2 text-left text-gray-800">
        <li>
          <b>Look for Distortions:</b> Pay close attention to hands, teeth, ears, and background elements. AI often struggles with these details.
        </li>
        <li>
          <b>Unnatural Symmetry:</b> AI-generated images can sometimes have elements that are too symmetrical or unnaturally repetitive.
        </li>
        <li>
          <b>Uncanny Valley:</b> Does something feel "off" about a person's face or expression? It might be AI-generated.
        </li>
        <li>
          <b>Strange Lighting/Shadows:</b> Inconsistent light sources or shadows that don't match the environment can be a clue.
        </li>
        <li>
          <b>Blurry or Inconsistent Text:</b> AI often struggles with generating clear, legible text in images.
        </li>
        <li>
          <b>Context is Key:</b> Does the content make sense in its given context? Is the source reliable?
        </li>
      </ul>
    </div>
  );

  return (
    <div className="flex flex-col items-center w-full">
      <p className="text-lg md:text-xl text-gray-600 mb-4">
        Question {currentQuestionIndex + 1} of {totalQuestions}
      </p>
      <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6 leading-relaxed">
        {currentMedia.title}
      </h2>

      {/* Main content area: Media & Buttons on left/top, Feedback/Tips on right/bottom */}
      <div className="flex flex-col lg:flex-row lg:gap-8 w-full items-start mt-4">
        {/* Media and Answer Buttons Section */}
        <div className="flex flex-col items-center w-full lg:w-2/3">
          <div className="w-full h-auto bg-gray-200 rounded-lg overflow-hidden shadow-md mb-8">
            {mediaLoadError ? (
              <div className="w-full h-64 flex flex-col items-center justify-center bg-red-100 text-red-700 text-center p-4">
                <p className="text-lg font-semibold mb-2">
                  Error loading media.
                </p>
                <p className="text-sm">
                  The file could not be loaded. Please try again or check the media link.
                </p>
              </div>
            ) : (
              <>
                {currentMedia.mediaType === 'image' && (
                  <img
                    src={currentMedia.mediaLink}
                    alt={currentMedia.title}
                    className="w-full h-auto object-contain"
                    onError={(e) => {
                      console.error("Image failed to load:", e);
                      e.target.onerror = null; // Prevent infinite loop if fallback also fails
                      setMediaLoadError(true);
                    }}
                  />
                )}
                {currentMedia.mediaType === 'video' && (
                  // Video Player
                  <video
                    src={currentMedia.mediaLink}
                    controls // Provides default video controls (play, pause, volume, etc.)
                    className="w-full h-auto max-h-[400px] object-contain bg-black" // Responsive video, max height to prevent overflow
                    onError={(e) => {
                      console.error("Video failed to load:", e);
                      e.target.onerror = null; // Prevent infinite loop if fallback also fails
                      setMediaLoadError(true);
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-4 w-full justify-center"> {/* Reduced mb to mb-4 to bring next button closer */}
            <button
              onClick={() => onAnswerClick(true)}
              disabled={showFeedback}
              className={`flex-1 px-6 py-3 rounded-full text-lg font-bold transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-4
                ${showFeedback ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 text-white shadow-lg focus:ring-green-300'}`}
            >
              AI Generated
            </button>
            <button
              onClick={() => onAnswerClick(false)}
              disabled={showFeedback}
              className={`flex-1 px-6 py-3 rounded-full text-lg font-bold transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-4
                ${showFeedback ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600 text-white shadow-lg focus:ring-red-300'}`}
            >
              Not AI Generated
            </button>
          </div>

          {/* Moved Next Media button here */}
          {showFeedback && !mediaLoadError && ( // Only show if feedback is visible and no media error
            <button
              onClick={onNextClick}
              className="mt-4 px-7 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-full shadow-lg transform transition duration-300 ease-in-out hover:scale-105 focus:outline-none focus:ring-4 focus:ring-indigo-300 w-full sm:w-auto" // w-full on mobile, auto on larger
            >
              {currentQuestionIndex < totalQuestions - 1 ? 'Next Media' : 'See Results'}
            </button>
          )}

        </div>

        {/* Dynamic Right Section: Feedback or Tips */}
        <div className="w-full lg:w-1/3 mt-6 lg:mt-0"> {/* Add margin top for mobile, reset for large */}
          {showFeedback ? (
            <div className={`p-5 rounded-lg shadow-inner w-full border animate-fadeIn ${isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <h3 className={`text-2xl font-bold mb-3 ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                {isCorrect ? 'Correct!' : 'Incorrect.'}
              </h3>
              <p className="text-gray-800 text-lg mb-4 text-left">{currentMedia.explanation}</p>
              <div className="text-gray-700 text-md text-left">
                <p className="font-semibold mb-2">Things to look out for:</p>
                <ul className="list-disc list-inside space-y-1">
                  {currentMedia.pointers.map((pointer, index) => (
                    <li key={index}>{pointer}</li>
                  ))}
                </ul>
              </div>
              {/* Removed the next button from here */}
            </div>
          ) : (
            tipsContent // Display tips when feedback is not shown
          )}
        </div>
      </div>
    </div>
  );
};

// Result Screen Component
const ResultScreen = ({ score, totalQuestions, onRestartQuiz }) => {
  const shareText = `I scored ${score}/${totalQuestions} on the AI Fake Detector Quiz! Test your skills here: [Your App URL]`;
  const appUrl = "https://example.com/ai-detector-quiz"; // Placeholder: Replace with your actual app URL

  // Function to open share links
  const openShareLink = (platform) => {
    let url = '';
    switch (platform) {
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareText.replace('[Your App URL]', appUrl))}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText.replace('[Your App URL]', appUrl))}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(appUrl)}&quote=${encodeURIComponent(shareText.replace('[Your App URL]', ''))}`;
        break;
      default:
        return;
    }
    window.open(url, '_blank');
  };

  return (
    <div className="flex flex-col items-center w-full">
      <h2 className="text-3xl md:text-4xl font-extrabold text-blue-800 mb-6">
        Quiz Completed!
      </h2>
      <p className="text-2xl md:text-3xl text-gray-700 mb-8">
        Your Score: <span className="font-bold text-blue-600">{score}</span> / {totalQuestions}
      </p>

      <p className="text-lg text-gray-700 mb-6">
        Great job! You've completed the quiz. Keep practicing to become an expert at
        spotting AI-generated content.
      </p>

      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Share Your Score:</h3>
        <div className="flex flex-wrap justify-center gap-4">
          <button
            onClick={() => openShareLink('whatsapp')}
            className="flex items-center space-x-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-full shadow-lg transform transition duration-300 ease-in-out hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-300"
          >
            <i className="fab fa-whatsapp text-xl"></i> {/* Font Awesome icon */}
            <span>WhatsApp</span>
          </button>
          <button
            onClick={() => openShareLink('twitter')}
            className="flex items-x-2 px-6 py-3 bg-blue-400 hover:bg-blue-500 text-white font-bold rounded-full shadow-lg transform transition duration-300 ease-in-out hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-200"
          >
            <i className="fab fa-twitter text-xl"></i> {/* Font Awesome icon */}
            <span>Twitter</span>
          </button>
          <button
            onClick={() => openShareLink('facebook')}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-full shadow-lg transform transition duration-300 ease-in-out hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-300"
          >
            <i className="fab fa-facebook text-xl"></i> {/* Font Awesome icon */}
            <span>Facebook</span>
          </button>
        </div>
      </div>

      <button
        onClick={onRestartQuiz}
        className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-full shadow-lg transform transition duration-300 ease-in-out hover:scale-105 focus:outline-none focus:ring-4 focus:ring-indigo-300"
      >
        Play Again
      </button>
    </div>
  );
};

export default App;
